import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { Flex } from 'rebass/styled-components';
import styled from 'styled-components';

interface ResizableLayoutProps {
  children: [ReactNode, ReactNode];
  initialLeftWidth?: number | string; // can now be '60%' or 300
  minLeftWidth?: number;
  minRightWidth?: number;
  triggerResize?: (width: number) => void
}

const Container = styled(Flex)`
  width: 100%;
  height: 100vh;
  user-select: none;
`;

const Panel = styled(Flex)`
  height: 100%;
  overflow: auto;
`;

const Resizer = styled.div`
  position: relative;
  width: 12px; // Wider hitbox
  cursor: col-resize;
  z-index: 10;
  background: transparent;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    background-color: #5f749738;
    border-radius: 1px;
    transition: background-color 0.2s ease;
  }

  &:hover:before {
    background-color: #5f7497;
  }

  &:active:before {
    background-color: white;
  }
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 24px;
    background-image: radial-gradient(#5f7497 1px, transparent 1px);
    background-size: 4px 6px;
    background-repeat: repeat-y;
  }
`;

const ResizableLayout: React.FC<ResizableLayoutProps> = ({
  children,
  initialLeftWidth = '60%',
  minLeftWidth = 150,
  minRightWidth = 150,
  triggerResize
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [leftWidth, setLeftWidth] = useState<number>(0);
  const isResizing = useRef<boolean>(false);
  const animationFrame = useRef<number | null>(null);

  // Convert percentage to pixel width (only on first render)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.getBoundingClientRect().width;

    let widthPx: number;

    if (typeof initialLeftWidth === 'string' && initialLeftWidth.endsWith('%')) {
      const percent = parseFloat(initialLeftWidth);
      widthPx = (percent / 100) * containerWidth;
    } else {
      widthPx = typeof initialLeftWidth === 'number' ? initialLeftWidth : parseInt(initialLeftWidth, 10);
    }

    setLeftWidth(Math.max(minLeftWidth, widthPx));
  }, [initialLeftWidth, minLeftWidth]);

  useEffect(() => {
    if (triggerResize) {
      triggerResize(leftWidth)
    }
  }, [leftWidth])

  const onMouseDown = () => {
    isResizing.current = true;
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isResizing.current || !containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const containerWidth = rect.width;
    const offsetX = e.clientX - rect.left;

    const newLeftWidth = Math.max(minLeftWidth, offsetX);
    const rightMinBoundary = containerWidth - minRightWidth;

    if (offsetX > rightMinBoundary) return;

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    animationFrame.current = requestAnimationFrame(() => {
      setLeftWidth(newLeftWidth);
    });
  };

  const onMouseUp = () => {
    isResizing.current = false;
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  if (!Array.isArray(children) || children.length !== 2) {
    console.warn('ResizableLayout expects exactly 2 children.');
    return null;
  }

  return (
    <Container ref={containerRef} flexDirection="row">
      <Panel width={`${leftWidth}px`}>
        {children[0]}
      </Panel>
      <Resizer onMouseDown={onMouseDown} />
      <Panel flex="1">
        {children[1]}
      </Panel>
    </Container>
  );
};

export default ResizableLayout;